import { useState, useCallback, useRef, useEffect } from "react";

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface UseMarqueeSelectOptions {
  containerRef: React.RefObject<HTMLElement | null>;
  cardSelector: string;
  onSelect: (ids: number[]) => void;
  enabled?: boolean;
  /** Key that activates marquee mode. Default: "Shift" */
  activationKey?: string;
}

interface UseMarqueeSelectResult {
  selectionRect: Rect | null;
  isSelecting: boolean;
  /** True while the activation key is held — used to render the overlay. */
  isMarqueeMode: boolean;
  /** Attach to the overlay's onMouseDown to start a drag. */
  onOverlayMouseDown: (e: React.MouseEvent) => void;
}

const rectsIntersect = (a: Rect, b: DOMRect, containerRect: DOMRect): boolean => {
  const bRel = {
    x: b.left - containerRect.left,
    y: b.top - containerRect.top,
    width: b.width,
    height: b.height,
  };
  return !(
    a.x + a.width < bRel.x ||
    bRel.x + bRel.width < a.x ||
    a.y + a.height < bRel.y ||
    bRel.y + bRel.height < a.y
  );
};

export const useMarqueeSelect = ({
  containerRef,
  cardSelector,
  onSelect,
  enabled = true,
  activationKey = "Shift",
}: UseMarqueeSelectOptions): UseMarqueeSelectResult => {
  const [selectionRect, setSelectionRect] = useState<Rect | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [isMarqueeMode, setIsMarqueeMode] = useState(false);
  const startRef = useRef<{ x: number; y: number } | null>(null);

  // Track activation key
  useEffect(() => {
    if (!enabled) {
      setIsMarqueeMode(false);
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === activationKey && !e.repeat) {
        setIsMarqueeMode(true);
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === activationKey) {
        setIsMarqueeMode(false);
      }
    };
    const handleBlur = () => setIsMarqueeMode(false);

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleBlur);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleBlur);
    };
  }, [enabled, activationKey]);

  /** Called by the overlay's onMouseDown. */
  const onOverlayMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      const container = containerRef.current;
      if (!container) return;
      e.preventDefault();

      const containerRect = container.getBoundingClientRect();
      startRef.current = {
        x: e.clientX - containerRect.left + container.scrollLeft,
        y: e.clientY - containerRect.top + container.scrollTop,
      };
    },
    [containerRef],
  );

  // Global mousemove / mouseup while dragging
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!startRef.current) return;
      const container = containerRef.current;
      if (!container) return;
      const containerRect = container.getBoundingClientRect();

      const currentX = e.clientX - containerRect.left + container.scrollLeft;
      const currentY = e.clientY - containerRect.top + container.scrollTop;

      const rect: Rect = {
        x: Math.min(startRef.current.x, currentX),
        y: Math.min(startRef.current.y, currentY),
        width: Math.abs(currentX - startRef.current.x),
        height: Math.abs(currentY - startRef.current.y),
      };

      if (rect.width > 3 || rect.height > 3) {
        setIsSelecting(true);
        setSelectionRect(rect);
      }
    },
    [containerRef],
  );

  const handleMouseUp = useCallback(() => {
    if (!startRef.current) return;

    if (isSelecting && selectionRect) {
      const container = containerRef.current;
      if (container) {
        const containerRect = container.getBoundingClientRect();
        const cards = container.querySelectorAll(cardSelector);
        const intersectingIds: number[] = [];

        cards.forEach((card) => {
          const cardRect = card.getBoundingClientRect();
          if (rectsIntersect(selectionRect, cardRect, containerRect)) {
            const id = card.getAttribute("data-image-id");
            if (id) intersectingIds.push(Number(id));
          }
        });

        if (intersectingIds.length > 0) {
          onSelect(intersectingIds);
        }
      }
    }

    startRef.current = null;
    setIsSelecting(false);
    setSelectionRect(null);
  }, [containerRef, cardSelector, selectionRect, isSelecting, onSelect]);

  useEffect(() => {
    if (!enabled) return;
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [enabled, handleMouseMove, handleMouseUp]);

  // Clean up drag state if marquee mode is exited mid-drag
  useEffect(() => {
    if (!isMarqueeMode && startRef.current) {
      startRef.current = null;
      setIsSelecting(false);
      setSelectionRect(null);
    }
  }, [isMarqueeMode]);

  return { selectionRect, isSelecting, isMarqueeMode, onOverlayMouseDown };
};
